import React, { useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useGetTrackingHistoryQuery } from '../../app/orderApi/OrderApi';
import CopyToClipboard from '../../components/shared/copyToClipboard/CopyToClipboard';
import { formatDashboardDateTime } from '../../helpers/dashboardTime';

const formatEventDate = (value) => formatDashboardDateTime(value, '');

// Kept separate from the customer/PO cards so future layout merges cannot
// silently remove carrier tracking along with unrelated customer fields.
export default function OrderLogisticsCard({ currentOrder }) {
    const shopifyId = currentOrder?.ShopifyId || currentOrder?.ShopifyID;
    const [showAllTracking, setShowAllTracking] = useState(false);
    const {
        currentData: trackingResponse,
        isLoading: isTrackingLoading,
        isFetching: isTrackingFetching,
        error: trackingError,
        refetch: refetchTracking,
    } = useGetTrackingHistoryQuery(
        { ShopifyID: shopifyId },
        { skip: !shopifyId, refetchOnMountOrArgChange: true }
    );
    useEffect(() => { setShowAllTracking(false); }, [shopifyId]);
    const logistics = trackingResponse?.data?.logistics;
    const trackingEvents = Array.isArray(logistics?.events) ? [...logistics.events].sort(
        (a, b) => (Date.parse(b.date) || 0) - (Date.parse(a.date) || 0)
    ) : [];
    const displayedTrackingEvents = showAllTracking ? trackingEvents : trackingEvents.slice(0, 3);
    return (
<Card className="p-3 mt-3 logistics-card">
                <div className="logistics-card-header">
                    <h6 className="mb-0">Logistics Information</h6>
                    <button
                        type="button"
                        className="logistics-refresh-button"
                        onClick={() => refetchTracking()}
                        disabled={!shopifyId || isTrackingFetching}
                    >
                        {isTrackingFetching ? "Refreshing..." : "Refresh"}
                    </button>
                </div>

                {isTrackingLoading || (isTrackingFetching && !trackingResponse) ? (
                    <div className="logistics-empty">Loading logistics information...</div>
                ) : trackingError ? (
                    <div className="logistics-empty logistics-error">
                        Unable to load logistics information.
                    </div>
                ) : !logistics?.trackingNumber ? (
                    <div className="logistics-empty">No tracking number yet.</div>
                ) : (
                    <>
                        <div className="logistics-summary">
                            <div>
                                <span className="logistics-label">Tracking number</span>
                                <div className="logistics-value-row">
                                    <strong>{logistics.trackingNumber}</strong>
                                    <CopyToClipboard value={logistics.trackingNumber} />
                                </div>
                            </div>
                            {(logistics.carrierName || logistics.providerName) && (
                                <div>
                                    <span className="logistics-label">Carrier</span>
                                    <div>{logistics.carrierName || logistics.providerName}</div>
                                </div>
                            )}
                        </div>

                        {displayedTrackingEvents.length > 0 ? (
                            <div className="logistics-timeline">
                                {displayedTrackingEvents.map((event, index) => (
                                    <div className="logistics-event" key={`${event.date || "event"}-${index}`}>
                                        <span className="logistics-event-dot" aria-hidden="true" />
                                        <div>
                                            <div className="logistics-event-status">
                                                {event.status || event.description || "Shipment update"}
                                            </div>
                                            {(event.location || event.date) && (
                                                <div className="logistics-event-meta">
                                                    {[event.location, event.date && formatEventDate(event.date)]
                                                        .filter(Boolean)
                                                        .join(" · ")}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="logistics-empty logistics-empty-with-number">
                                Carrier updates are not available yet. Please try refreshing later.
                            </div>
                        )}

                        {trackingEvents.length > 3 && (
                            <button
                                type="button"
                                className="logistics-toggle-button"
                                onClick={() => setShowAllTracking(value => !value)}
                            >
                                {showAllTracking ? "Show less" : `View all ${trackingEvents.length} updates`}
                            </button>
                        )}
                    </>
                )}
            </Card>
    );
}
